import { useMemo, useState, type FC } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { usePlatformsAutocompleteQuery } from "../hooks/usePlatformsAutocompleteQuery";

interface PlatformOption {
  id: string;
  title: string;
}

interface PlatformAutocompleteProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  disabled?: boolean;
  /**
   * Уже выбранная платформа (из запроса редактируемого юзера) — чтобы не
   * делать отдельный запрос "найти платформу по id" только ради подписи
   * значения, выбранного до того, как пользователь начал искать.
   */
  initialPlatform?: PlatformOption | null;
}

/**
 * Автокомплит выбора платформы (Query.platforms(rowsPerPage, filter: {search})).
 * Существующие `autocompletePlatforms`/`AutocompleteWithID` и декларативный
 * паттерн `type: "autocomplete", query: ...` (уже используемый в 5 других
 * админских формах) тоже подошли бы сюда и дали бы эквивалентное поведение
 * (debounce-поиск + подпись текущего значения через `$value`) — этот
 * самостоятельный компонент был сделан по образцу структуры уже
 * существующего LanguageAutocomplete, а не через общий AutocompleteField.
 * Возможно, стоит рассмотреть консолидацию через декларативный паттерн в
 * рамках отдельной задачи по рефакторингу.
 */
export const PlatformAutocomplete: FC<PlatformAutocompleteProps> = ({
  value,
  onChange,
  label,
  disabled,
  initialPlatform,
}) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue, 300);

  const { data, loading } = usePlatformsAutocompleteQuery({
    variables: { search: debouncedSearch || undefined },
    fetchPolicy: "cache-and-network",
  });

  const options = useMemo<PlatformOption[]>(() => {
    const loaded = data?.platforms?.nodes ?? [];
    if (
      initialPlatform &&
      value === initialPlatform.id &&
      !loaded.some((platform) => platform.id === initialPlatform.id)
    ) {
      return [initialPlatform, ...loaded];
    }
    return loaded;
  }, [data, initialPlatform, value]);

  const selectedOption =
    options.find((platform) => platform.id === value) ||
    (value && initialPlatform?.id === value ? initialPlatform : null) ||
    null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_event, newValue) => onChange(newValue ? newValue.id : null)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        // reason "reset" не фильтруем: MUI приводит текст инпута к label
        // выбранной опции именно через reset-вызов — с фильтром выбранное
        // значение (в т.ч. начальное из editData) выглядело вечно пустым.
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      loadingText="Загрузка..."
      noOptionsText="Платформы не найдены"
      disabled={disabled}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(opts) => opts}
      renderInput={(params) => (
        <TextField {...params} label={label} fullWidth />
      )}
    />
  );
};
